import React, { useEffect } from 'react';
import { IdentityProvider, useIdentityContext } from '@design-stack-vista/identity-provider';
import { createRoot } from 'react-dom/client';
import App from 'src/App';
import 'src/i18n';
import { isCareAgent, isProd, ownerId, windowExists } from 'src/utils/tenantUtil';
import { DebugContextProvider } from './providers/DebugContextProvider';
import { ErrorContextProvider } from './providers/ErrorContextProvider';
import { PriceContextProvider } from './providers/PriceContextProvider';
import { ProductContextProvider } from './providers/ProductContextProvider';
import { PromptContextProvider } from './providers/PromptContextProvider';
import { StudioContextProvider } from './providers/StudioContextProvider';
import { TrackingContextProvider } from './providers/TrackingContextProvider';
import { UploadContextProvider } from './providers/UploadContextProvider';
import { UploadProvider } from './providers/UploadProvider';
import { ValidationContextProvider } from './providers/ValidationContextProvider';
import { SwanConfiguration } from './SwanConfiguration';
import { currentCulture, currentLanguage } from './utils/cultureUtil';

function AppWrapper() {
  const { isIdentityInitialized } = useIdentityContext();

  useEffect(() => {
    const defaultLang = currentLanguage.toLocaleLowerCase() || 'en';
    document.documentElement.lang = defaultLang;
  }, []);

  return isIdentityInitialized ? (
    <DebugContextProvider>
      <PromptContextProvider>
        <ProductContextProvider>
          <TrackingContextProvider>
            <ErrorContextProvider>
              <PriceContextProvider>
                <UploadContextProvider>
                  <ValidationContextProvider>
                    <StudioContextProvider>
                      <UploadProvider>
                        <App />
                      </UploadProvider>
                    </StudioContextProvider>
                  </ValidationContextProvider>
                </UploadContextProvider>
              </PriceContextProvider>
            </ErrorContextProvider>
          </TrackingContextProvider>
        </ProductContextProvider>
      </PromptContextProvider>
    </DebugContextProvider>
  ) : (
    <></>
  );
}
async function renderApp(): Promise<void> {
  const container = document.getElementById('app');
  if (container) {
    const root = createRoot(container);
    root.render(
      <SwanConfiguration>
        <IdentityProvider
          locale={currentCulture.toLocaleLowerCase()}
          isProd={isProd()}
          isCareAgent={isCareAgent()}
          ownerId={ownerId}
          redirectUriOrigin={
            !isProd() && windowExists()
              ? `${window.location.origin}${window.location.pathname}`
              : `${window.location.origin}/upload-and-print`
          }
        >
          <AppWrapper />
        </IdentityProvider>
      </SwanConfiguration>
    );
  } else {
    console.error('Failed to find the app container');
  }
}

renderApp();
